import React, { useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import './ShoppingCart.css';

const ShoppingCart = () => {
  const {
    items,
    subtotal,
    isOpen,
    stockMessage,
    toggleCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    clearStockMessage
  } = useCart();

  // Formatear precio en COP
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Manejar checkout
  const handleCheckout = () => {
    // Navegar al checkout usando history API
    window.history.pushState({}, '', '/checkout');
    // Disparar evento para que AppRouter detecte el cambio
    window.dispatchEvent(new PopStateEvent('popstate'));
    // Cerrar el carrito
    toggleCart();
  };

  /** Ir a la tienda y cerrar el drawer (misma ruta que usa AppRouter). */
  const goToTienda = () => {
    window.history.pushState({}, '', '/tienda');
    window.dispatchEvent(new PopStateEvent('popstate'));
    toggleCart();
  };

  // Bloquear scroll del documento mientras el carrito está abierto (evita mover la página de fondo / gaps en móvil)
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevWidth = document.body.style.width;
    const prevTop = document.body.style.top;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Manejar cambio de cantidad
  const handleQuantityChange = async (item, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item.id, item.selectedSize, item.selectedColor);
    } else {
      // Llamar a updateQuantity que maneja la validación de stock internamente
      await updateQuantity(item.id, item.selectedSize, item.selectedColor, newQuantity);
    }
  };

  // Auto-ocultar mensaje de stock después de 5 segundos
  useEffect(() => {
    if (!stockMessage) return;
    const t = setTimeout(clearStockMessage, 5000);
    return () => clearTimeout(t);
  }, [stockMessage, clearStockMessage]);

  // Manejar eliminar item
  const handleRemoveItem = (item) => {
    removeFromCart(item.id, item.selectedSize, item.selectedColor);
  };

  // Manejar limpiar carrito
  const handleClearCart = () => {
    if (window.confirm('¿Estás seguro de que quieres vaciar el carrito?')) {
      clearCart();
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`cart-overlay ${isOpen ? 'open' : ''}`}
        onClick={toggleCart}
      />

      {/* Carrito - stopPropagation evita que clics se propaguen al overlay */}
      <div 
        className={`shopping-cart ${isOpen ? 'open' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-text">
            <p className="cart-header-eyebrow">Javiland · Tienda consciente</p>
            <h3>
              <i className="fas fa-shopping-bag" aria-hidden="true"></i>
              Tu carrito
            </h3>
            {items.length > 0 && (
              <p className="cart-header-sub">{items.length} {items.length === 1 ? 'artículo' : 'artículos'}</p>
            )}
          </div>
          <button type="button" className="close-cart" onClick={toggleCart} aria-label="Cerrar carrito">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Contenido */}
        <div className="cart-content">
          {items.length === 0 ? (
            <div className="empty-cart">
              <i className="fas fa-leaf" aria-hidden="true"></i>
              <h4>Tu carrito está vacío</h4>
              <p>Explora la tienda y elige piezas que resonen contigo. Estaremos aquí cuando quieras continuar.</p>
              <button 
                className="continue-shopping-btn"
                onClick={goToTienda}
              >
                Continuar comprando
              </button>
            </div>
          ) : (
            <div className="cart-items">
              <p className="cart-items-intro">Revisa tu selección antes de continuar. Cada pieza cuenta.</p>
              <button
                type="button"
                className="continue-shopping-outline"
                onClick={goToTienda}
              >
                <i className="fas fa-store" aria-hidden="true" />
                Continuar comprando
              </button>
              {stockMessage && (
                <div className="cart-stock-message">
                  <i className="fas fa-info-circle"></i>
                  <span>{stockMessage}</span>
                  <button type="button" className="cart-stock-message-close" onClick={clearStockMessage} aria-label="Cerrar">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
              {items.map((item) => {
                const lineTotal = (item.price || 0) * (item.quantity || 1);
                return (
                  <article
                    key={`${item.id}-${item.selectedSize}-${item.selectedColor}`}
                    className="cart-item"
                  >
                    <div className="cart-item-visual">
                      <img
                        src={item.image || '/images/placeholders/placeholder-product.png'}
                        alt={item.name || 'Producto'}
                        className="cart-item-image"
                        onError={(e) => {
                          e.target.src = '/images/placeholders/placeholder-product.png';
                        }}
                      />
                    </div>
                    <div className="cart-item-body">
                      <h4 className="cart-item-title">{item.name || 'Producto'}</h4>
                      {(item.selectedSize || item.selectedColor) && (
                        <p className="cart-item-variant">
                          <span className="cart-item-variant-label">Detalle</span>
                          {(item.selectedSize || 'Único')} · {(item.selectedColor || 'Único')}
                        </p>
                      )}
                      <div className="cart-item-pricing-row">
                        <div className="cart-item-price-container">
                          {item.originalPrice && item.originalPrice > item.price ? (
                            <>
                              <span className="cart-item-original-price">{formatPrice(item.originalPrice)}</span>
                              <span className="cart-item-unit">{formatPrice(item.price)} <span className="cart-item-unit-note">c/u</span></span>
                            </>
                          ) : (
                            <span className="cart-item-unit">{formatPrice(item.price)} <span className="cart-item-unit-note">c/u</span></span>
                          )}
                        </div>
                        <div className="cart-item-line-total">
                          <span className="cart-item-line-label">Subtotal línea</span>
                          <span className="cart-item-line-amount">{formatPrice(lineTotal)}</span>
                        </div>
                      </div>
                      <div className="cart-item-actions">
                        <div className="quantity-controls" role="group" aria-label="Cantidad">
                          <button
                            type="button"
                            className="quantity-btn quantity-btn-minus"
                            onClick={(e) => { e.stopPropagation(); handleQuantityChange(item, item.quantity - 1); }}
                            title="Disminuir cantidad"
                            aria-label="Disminuir cantidad"
                          >
                            <i className="fas fa-minus"></i>
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            type="button"
                            className="quantity-btn quantity-btn-plus"
                            onClick={(e) => { e.stopPropagation(); handleQuantityChange(item, item.quantity + 1); }}
                            title="Aumentar cantidad"
                            aria-label="Aumentar cantidad"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                        <button
                          type="button"
                          className="remove-item-btn"
                          onClick={() => handleRemoveItem(item)}
                          title="Quitar del carrito"
                        >
                          <i className="fas fa-trash-alt" aria-hidden="true"></i>
                          <span className="remove-item-label">Quitar</span>
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
              <div className="summary-info-box cart-next-step-in-list" aria-label="Siguiente paso">
                <div className="summary-info-icon">
                  <i className="fas fa-leaf" aria-hidden="true"></i>
                </div>
                <div className="summary-info-text">
                  <span className="summary-info-title">Siguiente paso</span>
                  <span className="summary-info-message">
                    En el checkout ingresas dirección y eliges pago. Ahí podrás aplicar cupones si tienes uno.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="cart-footer">
            <div className="cart-summary">
              {/* Mostrar ahorros si hay descuentos */}
              {(() => {
                const totalSavings = items.reduce((total, item) => {
                  if (item.originalPrice && item.originalPrice > item.price) {
                    const itemSavings = (item.originalPrice - item.price) * item.quantity;
                    return total + itemSavings;
                  }
                  return total;
                }, 0);
                
                if (totalSavings > 0) {
                  return (
                    <div className="summary-row summary-savings">
                      <span className="summary-label">Ahorros:</span>
                      <span className="summary-value savings-value">-{formatPrice(totalSavings)}</span>
                    </div>
                  );
                }
                return null;
              })()}
              
              <div className="summary-row summary-subtotal">
                <span className="summary-label">Subtotal:</span>
                <span className="summary-value">{formatPrice(subtotal)}</span>
              </div>
            </div>

            <div className="checkout-actions">
              <button
                className="checkout-btn"
                onClick={handleCheckout}
                disabled={items.length === 0}
              >
                <i className="fas fa-credit-card"></i>
                Proceder al Pago
              </button>
              
              <button
                className="clear-cart-btn"
                onClick={handleClearCart}
              >
                <i className="fas fa-trash"></i>
                Vaciar Carrito
              </button>
            </div>

            {/* Información de seguridad */}
            <div className="security-info">
              <i className="fas fa-shield-alt"></i>
              <div className="security-text">
                Tu información está protegida con encriptación SSL de 256 bits
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShoppingCart;






















